// File: src/components/Reveal/RevealIntroSequence.jsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import { ReactComponent as TribeLogo } from "../../assets/TribeLogo.svg";

/**
 * Reveal intro sequence (DIAGNOSTIC INSTRUMENTED)
 * - STEP 0: White canvas “IT’S A MATCH” (EXACT FoundMatch overlay style)
 * - STEP 1/3: Full-bleed photo + Ken Burns + meet panels
 * - STEP 2: Interstitial for multi-match (white/blue, smooth staged animation)
 * - Reduced-motion safety, image preload, precise chime sync
 */

const T = {
  unfurlIn: 820,
  unfurlHold: 520,
  meetHold: 4000,
  interExpand: 820,
  interType: 1100,
  interCollapse: 620,
};

function displayName(p) {
  const obj = (p && typeof p === "object") ? p : {};
  const fn = obj.firstName || "";
  const ln = obj.lastName ? `${obj.lastName.charAt(0).toUpperCase()}.` : "";
  return (fn || ln) ? `${fn} ${ln}`.trim() : "Your Match";
}

/* Logger */
function mkLogger() {
  if (!window.__RIS_SEQ) window.__RIS_SEQ = 0;
  const id = ++window.__RIS_SEQ;
  const base = `[RevealIntroSeq#${id}]`;
  const t0 = performance.now();
  const ts = () => (performance.now() - t0).toFixed(1).padStart(7, " ");
  return {
    log: (...a) => console.log(base, ts(), ...a),
    warn: (...a) => console.warn(base, ts(), ...a),
  };
}

export default function RevealIntroSequence({
  partners = [],
  totalStages = null,               // authoritative count (1 or 2)
  onComplete = () => {},
  soundUrl = "/ambient-flute-notification-3-185275.mp3",
  enableHaptics = false,
}) {
  const L = useRef(mkLogger()).current;

  useEffect(() => {
    L.log("MOUNT partners:", partners);
    L.log("MOUNT totalStages:", totalStages);
  }, []); // eslint-disable-line

  // Resolve partners, then compute "two" intent vs renderability
  const p1 = partners[0] || {};
  const p2 = partners[1] || null;

  // "We *intend* to run two-stage" (from server header or array length)
  const declaredTwo = Number(totalStages) >= 2;
  const wantTwo = declaredTwo || partners.length >= 2;

  // "We *can* render stage-2 face right now"
  const twoRenderable = wantTwo && !!p2;

  const prefersReduced = useMemo(
    () =>
      typeof window !== "undefined" &&
      window.matchMedia?.("(prefers-reduced-motion: reduce)").matches,
    []
  );

  useEffect(() => {
    L.log("derive two →", {
      declaredTwo,
      partnersLen: partners.length,
      wantTwo,
      twoRenderable,
      hasP2: !!p2,
    });
  }, [declaredTwo, partners.length, wantTwo, twoRenderable, p2]); // eslint-disable-line

  const [step, setStep] = useState(0);
  const [ready, setReady] = useState(false);

  const audioRef = useRef(null);
  const playedRef = useRef(false);
  function playChime() {
    if (playedRef.current) return;
    const a = audioRef.current || new Audio(soundUrl);
    audioRef.current = a;
    try { a.currentTime = 0; } catch {}
    a.play().catch((e) => L.warn("audio play rejected:", e?.message));
    if (enableHaptics && "vibrate" in navigator) navigator.vibrate?.(12);
    playedRef.current = true;
  }

  // Scroll lock
  useEffect(() => {
    const prev = document.documentElement.style.overflow;
    document.documentElement.style.overflow = "hidden";
    L.log("scroll lock applied");
    return () => {
      document.documentElement.style.overflow = prev;
      L.log("scroll lock restored");
    };
  }, []); // eslint-disable-line

  // Preload p1
  useEffect(() => {
    const src = p1?.profilePicUrl || "/default-avatar.png";
    L.log("p1 preload start:", src);
    const img = new Image();
    img.onload = () => { L.log("p1 preload onload"); setReady(true); };
    img.onerror = () => { L.warn("p1 preload onerror"); setReady(true); };
    img.src = src;
  }, [p1?.profilePicUrl]); // eslint-disable-line

  // Audio preload
  useEffect(() => {
    try {
      const a = new Audio(soundUrl);
      a.preload = "auto";
      audioRef.current = a;
      L.log("audio preloaded:", soundUrl);
    } catch (e) {
      L.warn("audio preload failed:", e?.message);
    }
  }, [soundUrl]); // eslint-disable-line

  // Reduced motion chime
  useEffect(() => {
    if (ready && step === 0 && prefersReduced) {
      L.log("reduced-motion: play chime on step 0");
      playChime();
    }
  }, [ready, step, prefersReduced]); // eslint-disable-line

  // Live state logs
  useEffect(() => {
    L.log("STATE:", {
      step,
      ready,
      declaredTwo,
      wantTwo,
      twoRenderable,
      partnersLen: partners.length,
      p1Pic: p1?.profilePicUrl,
      p2Pic: p2?.profilePicUrl,
    });
  }, [step, ready, declaredTwo, wantTwo, twoRenderable, partners.length, p1?.profilePicUrl, p2?.profilePicUrl]); // eslint-disable-line

  // Sequence timing + "wait-for-p2" bridge
  useEffect(() => {
    let tA, waitTick, waitKill;
    const go = (s, ms, tag = "") => {
      L.log(`schedule step ${s} in ${ms}ms`, tag);
      tA = setTimeout(() => {
        L.log(`advance → step ${s}`, tag);
        setStep(s);
      }, ms);
    };

    if (!ready) {
      L.log("not ready → no timers");
      return;
    }

    if (step === 0) {
      const ms = prefersReduced ? 300 : T.unfurlIn + T.unfurlHold;
      L.log("STEP 0 → schedule STEP 1", { ms });
      go(1, ms, "0→1");
    } else if (step === 1) {
      const ms = prefersReduced ? 500 : T.meetHold;
      L.log("STEP 1 (p1) → schedule", wantTwo ? "STEP 2" : "STEP 4", { ms, wantTwo, declaredTwo, partnersLen: partners.length });
      go(wantTwo ? 2 : 4, ms, "1→(2|4)");
    } else if (step === 2 && wantTwo) {
      // Interstitial should run if header declared two, even if p2 isn't loaded yet.
      // If p2 is ready, proceed normally after interstitial; else wait briefly for p2.
      const ms = prefersReduced ? 700 : 1400;
      L.log("STEP 2 (interstitial)", { twoRenderable, hasP2: !!p2, ms });

      if (twoRenderable) {
        go(3, ms, "2→3");
      } else {
        // Wait loop: poll for p2 up to ~4s, otherwise skip to done (4).
        const start = performance.now();
        waitTick = setInterval(() => {
          const elapsed = performance.now() - start;
          if (partners[1]) {
            L.log("p2 arrived during interstitial → advance to STEP 3");
            clearInterval(waitTick);
            if (waitKill) clearTimeout(waitKill);
            setStep(3);
          } else if (elapsed > 4000) {
            L.warn("p2 did not arrive in time → skip second meet");
            clearInterval(waitTick);
            setStep(4);
          }
        }, 150);

        // Safety kill (in case timers overlap)
        waitKill = setTimeout(() => {
          if (waitTick) clearInterval(waitTick);
        }, 4500);
      }
    } else if (step === 3) {
      const ms = prefersReduced ? 600 : T.meetHold;
      L.log("STEP 3 (p2) → schedule STEP 4", { ms, hasP2: !!p2 });
      go(4, ms, "3→4");
    } else if (step === 4) {
      L.log("STEP 4 (done) → onComplete()");
      try { onComplete?.(); } catch (e) { L.warn("onComplete error:", e?.message); }
    }

    return () => {
      if (tA) { clearTimeout(tA); L.log("cleanup: clear tA"); }
      if (waitTick) { clearInterval(waitTick); L.log("cleanup: clear waitTick"); }
      if (waitKill) { clearTimeout(waitKill); L.log("cleanup: clear waitKill"); }
    };
  }, [ready, step, wantTwo, twoRenderable, prefersReduced, partners, p2]); // eslint-disable-line

  /* ---------------------------- RENDER ---------------------------- */
  const MeetFullBleed = React.memo(({ person, label }) => {
    const safe = (person && typeof person === "object") ? person : {};
    const name = displayName(safe);
    const meta = [safe.gender || "", safe.age != null ? `${safe.age}` : ""]
      .filter(Boolean)
      .join(" ")
      .trim();
    const src = safe?.profilePicUrl || "/default-avatar.png";
    useEffect(() => {
      L.log(`render MeetFullBleed(${label})`, { name, src });
    }, [name, src]); // eslint-disable-line
    return (
      <div style={S.stage} aria-live="polite">
        <img
          src={src}
          onError={(e) => { e.currentTarget.src = "/default-avatar.png"; L.warn(`img error (${label}) → fallback`, { src }); }}
          alt=""
          style={{ ...S.bgImg }}
        />
        <div style={S.gradBottomStrong} />
        <div style={S.meetPanel}>
          <div style={S.meetNameBlue}>{name}</div>
          {meta && <div style={S.meetMeta}>{meta}</div>}
        </div>
      </div>
    );
  });

  const FirstStateBackdrop = React.memo(({ person, label }) => {
    const src = person?.profilePicUrl || "/default-avatar.png";
    useEffect(() => {
      L.log(`render Backdrop(${label})`, { src });
    }, [src]); // eslint-disable-line
    return (
      <>
        <img
          src={src}
          onError={(e) => { e.currentTarget.src = "/default-avatar.png"; L.warn(`backdrop error (${label}) → fallback`, { src }); }}
          alt=""
          style={{ ...S.bgImg }}
        />
        <div style={S.gradBottomSoft} />
        <div style={S.scrimTop} />
      </>
    );
  });

  L.log("RENDER", {
    step, ready, declaredTwo, wantTwo, twoRenderable,
    partnersLen: partners.length,
    p1HasPic: !!p1?.profilePicUrl,
    p2HasPic: !!p2?.profilePicUrl
  });

  return (
    <div style={S.full} className="reveal-intro-seq">
      {/* Consistent light-grey Tribe logo across ALL states */}
      <style>{`
        .reveal-intro-seq .tribe-grey :is(path,circle,rect,polygon,polyline,ellipse) { fill: #ffff !important; }
        .reveal-intro-seq .tribe-grey *[fill="none"] { fill: none !important; }
        .reveal-intro-seq .tribe-grey * { stroke: none !important; }
      `}</style>

      {/* Fixed, centered Tribe logo (light grey everywhere) */}
      <TribeLogo className="tribe-grey" style={S.logoFixed} aria-label="Tribe" />

      {/* STEP 0 — EXACT FoundMatch overlay (white) */}
      {ready && step === 0 && (
        <div style={stylesMatch.matchOverlay}>
          <div style={stylesMatch.overlayContent}>
            <div className="itsA">IT&apos;S A</div>
            <div className="matchMain">
              <div
                className="matchFilled"
                onAnimationStart={() => { L.log("Step0 anim start → playChime"); playChime(); }}
              >
                MATCH
              </div>
              <div className="hollowWrapper" aria-hidden="true">
                {Array.from({ length: 11 })
                  .map((_, i) => i)
                  .reverse()
                  .map((i) => (
                    <div
                      key={i}
                      className="matchHollow"
                      style={{
                        animationDelay: `${(11 - 1 - i) * 0.1667}s`,
                        zIndex: -(11 - i),
                      }}
                    >
                      MATCH
                    </div>
                  ))}
              </div>
            </div>
          </div>

          {/* Overlay-only styles (EXACT from FoundMatch) */}
          <style>{`
            @keyframes fadeSlide {
              from { opacity: 0; transform: translateY(-15px); }
              to   { opacity: 1; transform: translateY(0); }
            }
            .itsA {
              font-size: 4rem;
              font-weight: 700;
              color: #3498db;
              animation: fadeSlide 0.0667s ease-in-out forwards;
              margin-bottom: -40px;
              font-family: "Arial Black", sans-serif;
              letter-spacing: .02em;
            }
            .matchMain { text-align: center; }
            .matchFilled {
              font-size: 6.5rem;
              font-weight: 900;
              color: #3498db;
              animation: fadeSlide 0.0333s ease-in-out forwards;
              margin-bottom: 0;
              font-family: "Arial Black", sans-serif;
              letter-spacing: .01em;
            }
            .hollowWrapper {
              display: flex;
              flex-direction: column;
              align-items: center;
              pointer-events: none;
            }
            .matchHollow {
              position: relative;
              font-size: 6.5rem;
              font-weight: 900;
              color: transparent;
              -webkit-text-stroke: 2px rgba(52,152,219,0.3);
              text-stroke: 2px rgba(52,152,219,0.3);
              font-family: "Arial Black", sans-serif;
              opacity: 0;
              animation: fadeSlide 0.0333s ease-in-out forwards;
              margin-top: -80px;
              clip-path: inset(35% 0 0 0);
              letter-spacing: .01em;
            }
            @media (max-width: 412px) {
              .itsA { font-size: 3.4rem; }
              .matchFilled, .matchHollow { font-size: 6rem; }
            }
          `}</style>
        </div>
      )}

      {/* STEP 1 — Meet #1 (full-bleed) */}
      {ready && step === 1 && <MeetFullBleed person={p1} label="p1" />}

      {/* STEP 2 — Interstitial (white/blue, smooth staged animation) */}
      {ready && step === 2 && wantTwo && (
        <div style={S.oneMoreWhiteStage}>
          <div style={S.oneMoreWrap}>
            <div style={S.oneMoreHeading} className="omt-eyebrow">ONE MORE THING...</div>
            <div style={S.oneMoreOutlined} className="omt-outlined">YOU HAVE ANOTHER</div>
            <div style={S.oneMoreFilled} className="omt-filled">MATCH!</div>
          </div>

          {/* Sleek staggered entrance (eyebrow → outlined → filled) */}
          <style>{`
            @keyframes omtFadeUp {
              from { opacity: 0; transform: translateY(12px) scale(0.995); }
              to   { opacity: 1; transform: translateY(0) scale(1); }
            }
            .omt-eyebrow {
              animation: ${prefersReduced ? "none" : "omtFadeUp 320ms cubic-bezier(.22,.61,.36,1) forwards"};
              animation-delay: 40ms;
              opacity: 0;
            }
            .omt-outlined {
              animation: ${prefersReduced ? "none" : "omtFadeUp 360ms cubic-bezier(.22,.61,.36,1) forwards"};
              animation-delay: 180ms;
              opacity: 0;
            }
            .omt-filled {
              animation: ${prefersReduced ? "none" : "omtFadeUp 420ms cubic-bezier(.22,.61,.36,1) forwards"};
              animation-delay: 340ms;
              opacity: 0;
            }
          `}</style>
        </div>
      )}

      {/* STEP 3 — Meet #2 */}
      {ready && step === 3 && twoRenderable && <MeetFullBleed person={p2} label="p2" />}

      {/* keyframes used in other steps */}
      <style>{KFS()}</style>
    </div>
  );
}

/* -------------------------------- Styles -------------------------------- */

const S = {
  full: {
    position: "fixed",
    inset: 0,
    background: "#000",
    zIndex: 99999,
    overflow: "hidden",
    WebkitFontSmoothing: "antialiased",
    MozOsxFontSmoothing: "grayscale",
    fontFamily:
      "Inter, system-ui, -apple-system, Segoe UI, Roboto, Helvetica, Arial, sans-serif",
  },

  logoFixed: {
    position: "fixed",
    top: "calc(env(safe-area-inset-top, 0px) + 12px)",
    left: "50%",
    transform: "translateX(-50%)",
    width: 64,
    height: "auto",
    zIndex: 3,
    pointerEvents: "none",
  },

  stage: { position: "absolute", inset: 0 },
  bgImg: {
    position: "absolute",
    inset: 0,
    width: "100%",
    height: "100%",
    objectFit: "cover",
    display: "block",
    willChange: "transform, opacity",
    transform: "translateZ(0)",
  },

  gradBottomSoft: {
    position: "absolute",
    left: 0,
    right: 0,
    bottom: 0,
    height: "56%",
    background:
      "linear-gradient(to top, rgba(0,0,0,0.70) 0%, rgba(0,0,0,0.28) 48%, rgba(0,0,0,0) 100%)",
    pointerEvents: "none",
  },
  gradBottomStrong: {
    position: "absolute",
    left: 0,
    right: 0,
    bottom: 0,
    height: "46%",
    background:
      "linear-gradient(to top, rgba(0,0,0,0.85) 0%, rgba(0,0,0,0.30) 60%, rgba(0,0,0,0) 100%)",
    pointerEvents: "none",
  },
  scrimTop: {
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    height: 140,
    background:
      "linear-gradient(to bottom, rgba(0,0,0,0.58) 0%, rgba(0,0,0,0) 100%)",
    pointerEvents: "none",
  },

  meetPanel: {
    position: "absolute",
    left: "min(4vw, 24px)",
    bottom: "max(8px, env(safe-area-inset-bottom, 0px) + 18px)",
    padding: "0px 8px",
    borderRadius: 10,
    display: "grid",
    gap: 8,
    maxWidth: "88vw",
  },
  meetNameBlue: {
    fontSize: "3.4rem",
    fontWeight: 900,
    margin: 0,
    lineHeight: 1.15,
    textAlign: "left",
    letterSpacing: "-0.015em",
    color: "#ffff",
  },
  meetMeta: {
    fontSize: "2rem",
    fontWeight: 700,
    margin: 0,
    opacity: 0.96,
    color: "#ffff",
  },

  /* Interstitial (styles kept from your version) */
  interRoot: {
    position: "relative",
    width: "min(92vw, 560px)",
    height: "min(72vh, 560px)",
    borderRadius: 24,
    overflow: "hidden",
    background: "#000",
    boxShadow: "0 20px 44px rgba(0,0,0,0.38)",
    margin: "0 auto",
    top: "50%",
    transform: "translateY(-50%)",
  },
  interBackdrop: {
    position: "absolute",
    inset: 0,
    background: "linear-gradient(to top, rgba(0,0,0,1) 0%, rgba(0,0,0,1) 100%)",
    transformOrigin: "bottom",
    animation: "expandUp 820ms ease forwards",
  },
  interTextWrap: {
    position: "absolute",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    color: "#fff",
    width: "min(92vw, 740px)",
    textAlign: "center",
    zIndex: 3,
    display: "grid",
    gap: 14,
  },
  interHeading: {
    fontWeight: 900,
    letterSpacing: "0.08em",
    fontSize: "clamp(1.75rem, 3.6vw, 2rem)",
    textTransform: "uppercase",
  },
  interOutlined: {
    fontWeight: 900,
    fontSize: "clamp(4rem, 10.5vw, 6.2rem)",
    lineHeight: 1.05,
    letterSpacing: "-0.01em",
    color: "#fff",
    marginBottom: -8,
  },

  oneMoreWhiteStage: {
    position: "absolute",
    inset: 0,
    background: "#fff",
    display: "grid",
    placeItems: "center",
  },
  oneMoreWrap: {
    textAlign: "center",
    width: "min(92vw, 740px)",
    color: "#0b0b0b",
  },
  oneMoreHeading: {
    fontFamily: "Inter, system-ui, -apple-system, Segoe UI, Roboto, Helvetica, Arial, sans-serif",
    fontWeight: 900,
    letterSpacing: "0.10em",
    textTransform: "uppercase",
    color: "#3498db",
    fontSize: "clamp(2.1rem, 3.6vw, 1.95rem)",
    lineHeight: 1.1,
    margin: "0 0 6px 0",
  },
  oneMoreOutlined: {
    fontFamily: "Inter, system-ui, -apple-system, Segoe UI, Roboto, Helvetica, Arial, sans-serif",
    fontWeight: 900,
    textTransform: "uppercase",
    color: "#fff",
    fontSize: "clamp(4.7rem, 10vw, 6rem)",
    lineHeight: 1.05,
    letterSpacing: "-0.012em",
    margin: "0 0 -6px 0",
    textShadow: [
      "0  2px 0 rgba(52,152,219,0.65)",
      "0 -2px 0 rgba(52,152,219,0.65)",
      "2px  0 0 rgba(52,152,219,0.65)",
      "-2px 0 0 rgba(52,152,219,0.65)",
      "1.5px 1.5px 0 rgba(52,152,219,0.65)",
      "-1.5px 1.5px 0 rgba(52,152,219,0.65)",
      "1.5px -1.5px 0 rgba(52,152,219,0.65)",
      "-1.5px -1.5px 0 rgba(52,152,219,0.65)"
    ].join(", "),
  },
  oneMoreFilled: {
    fontFamily: "Inter, system-ui, -apple-system, Segoe UI, Roboto, Helvetica, Arial, sans-serif",
    fontWeight: 900,
    textTransform: "uppercase",
    color: "#3498db",
    fontSize: "clamp(6rem, 10.5vw, 6.4rem)",
    lineHeight: "1.03",
    letterSpacing: "-0.012em",
  },
  interFilled: {
    fontWeight: 900,
    fontSize: "clamp(5rem, 10.5vw, 6.2rem)",
    lineHeight: "1.05",
    letterSpacing: "-0.01em",
    color: "#fff",
    textTransform: "uppercase",
  },
};

const stylesMatch = {
  matchOverlay: {
    position: "absolute",
    inset: 0,
    backgroundColor: "#fff",
    zIndex: 9999,
    display: "flex",
    alignItems: "flex-start",
    justifyContent: "center",
    paddingTop: "5%",
  },
  overlayContent: { textAlign: "center", width: "100%" },
};

function KFS() {
  return `
  @keyframes fadeSlide {
    from { opacity: 0; transform: translateY(-16px); }
    to   { opacity: 1; transform: translateY(0); }
  }
  @keyframes expandUp {
    0%   { transform: scaleY(0); transform-origin: bottom; }
    100% { transform: scaleY(1); transform-origin: bottom; }
  }
  `;
}
